<template>
    <div data-testid="cambio-datos-paso-resumen">
        <CardFront>

            <div data-testid="cambio-datos-resumen-total" class="tw:mb-6 tw:pb-6 tw:border-b tw:border-slate-100">
                <div class="tw:flex tw:justify-between tw:items-center">
                    <p class="tw:text-sm tw:text-slate-500 tw:m-0">
                        {{ $t('perfil.cambio_datos.total_modificacion') }}
                    </p>
                    <span data-testid="cambio-datos-resumen-total-pagar" class="tw:text-red-500 tw:font-bold tw:text-2xl tw:ml-4">
                        {{ formatearPrecio(totalCambios) }}
                    </span>
                </div>
            </div>

            <h4 class="tw:flex tw:items-center tw:gap-2 tw:text-base tw:font-bold tw:text-slate-800 tw:m-0 tw:mb-5">
                <i class="material-icons tw:text-lg tw:text-slate-600">credit_card</i>
                {{ $t('perfil.cambio_datos.info_pago_titulo') }}
            </h4>

            <div v-if="loading" class="tw:mx-auto tw:w-[325px] tw:h-[523px] tw:overflow-hidden tw:border tw:border-slate-200 tw:flex tw:flex-col tw:animate-pulse">
                <div class="tw:h-[52px] tw:bg-slate-200 tw:shrink-0"></div>
                <div class="tw:flex-1 tw:bg-white tw:flex tw:flex-col tw:items-center tw:px-6 tw:pt-5 tw:pb-4 tw:gap-3">
                    <div class="tw:h-3 tw:w-4/5 tw:bg-slate-200 tw:rounded"></div>

                    <div class="tw:h-10 tw:w-2/5 tw:bg-slate-200 tw:rounded tw:mt-1"></div>

                    <div class="tw:flex tw:items-center tw:gap-3 tw:w-full tw:justify-center tw:mt-1">
                        <div class="tw:w-10 tw:h-7 tw:bg-slate-200 tw:rounded tw:shrink-0"></div>
                        <div class="tw:h-3 tw:w-32 tw:bg-slate-200 tw:rounded"></div>
                    </div>

                    <div class="tw:h-3 tw:w-28 tw:bg-slate-200 tw:rounded"></div>

                    <div class="tw:flex tw:items-start tw:gap-2 tw:w-full tw:mt-2">
                        <div class="tw:w-3 tw:h-3 tw:bg-slate-200 tw:rounded-full tw:shrink-0 tw:mt-0.5"></div>
                        <div class="tw:flex tw:flex-col tw:gap-1 tw:flex-1">
                            <div class="tw:h-2.5 tw:w-full tw:bg-slate-200 tw:rounded"></div>
                            <div class="tw:h-2.5 tw:w-3/4 tw:bg-slate-200 tw:rounded"></div>
                        </div>
                    </div>

                    <div class="tw:flex-1"></div>

                    <div class="tw:w-24 tw:h-8 tw:bg-slate-200 tw:rounded tw:border tw:border-slate-300"></div>

                    <div class="tw:flex tw:flex-col tw:gap-1 tw:w-full tw:items-center">
                        <div class="tw:h-2.5 tw:w-4/5 tw:bg-slate-200 tw:rounded"></div>
                        <div class="tw:h-2.5 tw:w-3/5 tw:bg-slate-200 tw:rounded"></div>
                    </div>

                    <div class="tw:w-full tw:h-px tw:bg-slate-200 tw:mt-2"></div>

                    <div class="tw:flex tw:gap-3 tw:w-full tw:justify-between">
                        <div class="tw:h-9 tw:w-[44%] tw:bg-slate-200 tw:rounded tw:shrink-0"></div>
                        <div class="tw:h-9 tw:w-[44%] tw:bg-slate-200 tw:rounded tw:shrink-0"></div>
                    </div>
                </div>
            </div>

            <!-- Redsys: redirección a TPV -->
            <template v-else-if="datosCompra?.formulario">
                <div class="tw:flex tw:flex-col tw:items-center tw:gap-4 tw:py-8">
                    <i class="material-icons tw:text-5xl tw:text-slate-300">payment</i>
                    <p class="tw:text-sm tw:text-slate-600 tw:text-center">
                        {{ $t('perfil.cambio_datos.redirigiendo_pasarela') }}
                    </p>
                    <div ref="redsysContainer"></div>
                </div>
            </template>

            <div v-else-if="iframe" class="tw:flex tw:justify-center tw:mb-4">
                <iframe
                    data-testid="cambio-datos-iframe-3ds"
                    :src="iframe"
                    width="325"
                    height="523"
                    sandbox="allow-top-navigation allow-scripts allow-same-origin allow-forms"
                    class="tw:border-0"
                ></iframe>
            </div>

            <PagoPaycometForm
                v-if="datosCompra?.jet_id && !loading && !iframe"
                ref="pagoFormRef"
                :jet_id="datosCompra.jet_id"
                @payToken="procesarPago"
            />

            <div
                v-if="errorMsg"
                data-testid="cambio-datos-pago-error"
                class="tw:text-sm tw:text-red-600 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-xl tw:px-4 tw:py-3 tw:mb-4"
            >
                {{ errorMsg }}
            </div>

            <Button
                v-if="!iframe && !loading && !datosCompra?.formulario"
                data-testid="cambio-datos-btn-pagar"
                variant="alt"
                custom-class="tw:w-full tw:py-3.5 tw:text-sm tw:rounded-xl tw:mb-3"
                @click="submitForm"
            >
                {{ $t('perfil.cambio_datos.finalizar_pagar', { total: formatearPrecio(totalCambios) }) }}
                <i class="material-icons tw:text-sm">lock</i>
            </Button>

            <div class="tw:flex tw:justify-center">
                <Button variant="ghost" custom-class="tw:w-full tw:py-3.5 tw:text-slate-400 tw:hover:text-slate-600" @click="emit('cancelar')">
                    {{ $t('perfil.cambio_datos.cancelar_operacion') }}
                </Button>
            </div>

        </CardFront>

        <div class="tw:flex tw:items-start tw:sm:items-center tw:gap-2 tw:mt-4 tw:px-2">
            <i class="material-icons tw:text-slate-400 tw:text-base tw:mt-0.5 tw:shrink-0">shield</i>
            <p class="tw:text-xs tw:text-slate-400 tw:m-0 tw:pt-1 tw:leading-relaxed">
                {{ $t('perfil.cambio_datos.seguridad_texto') }}
            </p>
        </div>
    </div>
</template>

<script setup>
    import { ref, watch, onMounted, onUnmounted, computed } from 'vue';
    import { useFilters } from '@/composables/front/formatters/useFilters';
    import CardFront from '@/modules/front/ui/CardFront.vue';
    import Button from '@/modules/front/ui/Button.vue';
    import PagoPaycometForm from '@/modules/front/perfil/components/CambiarDatos/PagoPaycometForm.vue';
    import { usePagoPaycomet } from '@/composables/front/perfil/usePagoPaycomet.js';
    import { t } from '@/utils/i18n-helper';

    const { formatearPrecio } = useFilters();

    const props = defineProps({
        pedido:                { type: Object, required: true },
        entradasSeleccionadas: { type: Array,  required: true },
        datosNuevos:           { type: Object, required: true },
        datosCompra:           { type: Object, required: true },
        totalCambios:          { type: Number, required: true },
    });

    const emit = defineEmits(['volver', 'cancelar', 'pago-completado']);

    const pagoFormRef = ref(null);
    const redsysContainer = ref(null);
    let redsysTimeout = null;
    let redsysSubmitted = false;

    const { loading, iframe, errorMsg: paycometErrorMsg, procesarPago } = usePagoPaycomet(
        { session_id: props.datosCompra.session_id, jet_id: props.datosCompra.jet_id },
        () => emit('pago-completado')
    );
    const redsysErrorMsg = ref('');
    const errorMsg = computed(() => redsysErrorMsg.value || paycometErrorMsg.value);

    const triggerRedsys = formHtml => {
        if (!formHtml || redsysSubmitted || !redsysContainer.value) return;
        redsysSubmitted = true;
        redsysContainer.value.innerHTML = formHtml;
        redsysTimeout = setTimeout(() => {
            const form = redsysContainer.value.querySelector('form');
            if (form) {
                form.submit();
            } else {
                redsysErrorMsg.value = t('perfil.cambio_datos.error_procesar_pago');
            }
        }, 100);
    };

    onMounted(() => {
        if (props.datosCompra?.formulario) {
            triggerRedsys(props.datosCompra.formulario);
        }
    });

    watch(() => props.datosCompra?.formulario, val => {
        if (val) triggerRedsys(val);
    });

    onUnmounted(() => {
        if (redsysTimeout) clearTimeout(redsysTimeout);
    });

    const submitForm = () => {
        pagoFormRef.value?.submit();
    };
</script>
